import React, { useState } from 'react';
import { getSupabaseUrl } from '../supabase/client';

interface TestCaseResult {
  caseIndex: number;
  passed: boolean;
  actualOutput: string;
  expectedOutput: string;
  executionTime: number;
  errorMessage: string | null;
  scoreWeight: number;
}

interface TestSummary {
  total: number;
  passed: number;
  failed: number;
  totalScore: number;
  maxScore: number;
}

export default function TestValidateTestCases() {
  const [code, setCode] = useState(`n = int(input())
print(n * 2)`);
  const [testCases, setTestCases] = useState([
    { stdin: '5', stdout: '10', scoreWeight: 1 },
    { stdin: '10', stdout: '20', scoreWeight: 1 },
    { stdin: '0', stdout: '0', scoreWeight: 1 },
  ]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    results: TestCaseResult[];
    summary: TestSummary;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRunTest = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const supabaseUrl = getSupabaseUrl();

      const response = await fetch(`${supabaseUrl}/functions/v1/code-grading`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'validate_test_cases',
          code,
          testCases,
          timeLimitMs: 1000,
          memoryLimitMb: 128,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '执行失败');
      }

      setResult(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : '未知错误';
      setError(message);
      console.error('测试失败:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">测试用例验证功能测试</h1>

        {/* 代码输入 */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Python代码</h2>
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="w-full h-40 p-4 border border-gray-300 rounded font-mono text-sm focus:border-blue-500 focus:outline-none"
            placeholder="输入Python代码..."
          />
        </div>

        {/* 测试用例 */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">测试用例 ({testCases.length}个)</h2>
          <div className="space-y-3">
            {testCases.map((tc, idx) => (
              <div key={idx} className="flex gap-4 items-center p-3 bg-gray-50 rounded">
                <span className="text-sm font-medium text-gray-600 w-16">用例{idx + 1}</span>
                <div className="flex-1">
                  <label className="text-xs text-gray-500">输入:</label>
                  <input
                    type="text"
                    value={tc.stdin}
                    onChange={(e) => {
                      const newCases = [...testCases];
                      newCases[idx].stdin = e.target.value;
                      setTestCases(newCases);
                    }}
                    className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-gray-500">期望输出:</label>
                  <input
                    type="text"
                    value={tc.stdout}
                    onChange={(e) => {
                      const newCases = [...testCases];
                      newCases[idx].stdout = e.target.value;
                      setTestCases(newCases);
                    }}
                    className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 运行按钮 */}
        <button
          onClick={handleRunTest}
          disabled={loading}
          className={`w-full py-3 rounded-lg font-semibold text-white transition-colors ${
            loading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {loading ? '执行中...' : '运行测试'}
        </button>

        {/* 错误信息 */}
        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="text-red-800 font-semibold mb-2">❌ 执行失败</h3>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* 测试结果 */}
        {result && (
          <div className="mt-6 space-y-4">
            {/* 摘要 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">测试摘要</h2>
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded">
                  <div className="text-2xl font-bold text-blue-600">{result.summary.total}</div>
                  <div className="text-sm text-gray-600">总用例数</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded">
                  <div className="text-2xl font-bold text-green-600">{result.summary.passed}</div>
                  <div className="text-sm text-gray-600">通过</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded">
                  <div className="text-2xl font-bold text-red-600">{result.summary.failed}</div>
                  <div className="text-sm text-gray-600">失败</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded">
                  <div className="text-2xl font-bold text-purple-600">
                    {result.summary.totalScore}/{result.summary.maxScore}
                  </div>
                  <div className="text-sm text-gray-600">得分</div>
                </div>
              </div>
            </div>

            {/* 详细结果 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">详细结果</h2>
              <div className="space-y-3">
                {result.results.map((r) => (
                  <div
                    key={r.caseIndex}
                    className={`p-4 rounded-lg border-2 ${
                      r.passed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{r.passed ? '✓' : '✗'}</span>
                        <span className="font-semibold">
                          用例 {r.caseIndex + 1}
                        </span>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            r.passed
                              ? 'bg-green-200 text-green-800'
                              : 'bg-red-200 text-red-800'
                          }`}
                        >
                          {r.passed ? '通过' : '失败'}
                        </span>
                      </div>
                      <span className="text-sm text-gray-600">
                        耗时: {r.executionTime}ms
                      </span>
                    </div>

                    {!r.passed && r.errorMessage && (
                      <div className="mb-2 p-2 bg-red-100 rounded text-sm text-red-700">
                        错误: {r.errorMessage}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">期望输出:</span>
                        <pre className="mt-1 p-2 bg-white rounded border border-gray-200 overflow-x-auto">
                          {r.expectedOutput}
                        </pre>
                      </div>
                      <div>
                        <span className="text-gray-600">实际输出:</span>
                        <pre
                          className={`mt-1 p-2 rounded border overflow-x-auto ${
                            r.passed
                              ? 'bg-green-100 border-green-200'
                              : 'bg-red-100 border-red-200'
                          }`}
                        >
                          {r.actualOutput}
                        </pre>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
